import appendQueryParams from "@/utils/appendQueryParams";
import { QueryFunctionContext } from "@tanstack/react-query";
import client from "../client";

export function createLineItem({
  campaignId,
  payload,
}: {
  campaignId: number | string;
  payload: any;
}) {
  return client.post(`/campaigns/${campaignId}/lineItems`, payload);
}
export function createFacebookLineItem({
  payload,
}: {
  campaignId: number | string;
  payload: any;
}) {
  return client.post(`/line-item`, payload);
}
export function editFacebookLineItem({
  payload,
  lineItemId,
}: {
  lineItemId: number | string;
  payload: any;
}) {
  return client.patch(`/line-item/${lineItemId}`, payload);
}
export function editLineItem({
  lineItemId,
  payload,
}: {
  lineItemId: number | string;
  payload: any;
}) {
  return client.patch(`/lineItems/${lineItemId}`, payload);
}
export function getLineItems(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(`/lineItems`, data.meta?.queryParams ?? {})
  );
}
export function getFacebookLineItem(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(
      `/line-item/facebook/${data?.meta?.id}`,
      data.meta?.queryParams ?? {}
    )
  );
}
export function getExternalLineItem(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(`/lineItems/external`, data.meta?.queryParams ?? {})
  );
}
export function getLinkedinLineItems(data: QueryFunctionContext) {
  return client.get(
    appendQueryParams(`linkedin/campaigns`, data.meta?.queryParams ?? {})
  );
}
export function deleteBeeswaxLineItem(
  id: number | string
) {
  return client.delete(`/line-item/display/${id}`);
}
export function validateDisplayLineItem(
  id: number,
  payload: Record<string, any>,
  context: QueryFunctionContext
) {
  const controller = new AbortController();
  const { signal } = context;
  signal?.addEventListener("abort", () => {
    controller.abort();
  });
  try {
    return client.post(`/line-item/display/validate/${id}`, payload, {
      signal: controller.signal,
    });
  } finally {
    signal?.removeEventListener("abort", () => {
      controller.abort();
    });
  }
}

export interface UpdateCreativeAssociationsPayload {
  lineItemId: number | string;
  creatives: Array<{
    internalAdId: number;
    active: boolean;
    // Association weight (1-100) + flight dates ("YYYY-MM-DD HH:mm:ss", America/New_York).
    // null clears a previously-set value; omitted keeps the Beeswax default.
    weight?: number | null;
    startDate?: string | null;
    endDate?: string | null;
  }>;
}

export interface UpdateCreativeAssociationsResponse {
  success: boolean;
  created: Array<{ internalAdId: number; beeswaxAdAssociationId: number; active: boolean }>;
  updated: Array<{ internalAdId: number; beeswaxAdAssociationId: number; active: boolean }>;
  deleted: Array<{ internalAdId: number; beeswaxAdAssociationId: number }>;
  failed: {
    create: Array<{ internalAdId: number; error: string }>;
    update: Array<{ internalAdId: number; beeswaxAdAssociationId: number; error: string }>;
    delete: Array<{ internalAdId: number; beeswaxAdAssociationId: number; error: string }>;
  };
  lineItemDeactivated: boolean;
}

export function updateCreativeAssociations({
  lineItemId,
  creatives,
}: UpdateCreativeAssociationsPayload) {
  return client.patch<UpdateCreativeAssociationsResponse>(
    `/line-item/display/${lineItemId}/creative-associations`,
    { creatives }
  );
}

export interface BeeswaxAssociation {
  internalAdId: number | null;
  beeswaxAdAssociationId: number;
  active: boolean;
  // From Beeswax v2: weight (int 1-100, nullable) + flight dates ("YYYY-MM-DD HH:mm:ss").
  weight?: number | null;
  startDate?: string | null;
  endDate?: string | null;
}

export function getCreativeAssociations(lineItemId: number | string): Promise<BeeswaxAssociation[]> {
  return client.get(`/line-item/display/${lineItemId}/creative-associations`);
}

export interface DuplicateLineItemPayload {
  targetInternalCampaignId: number; // internal Campaigns.id; default = source campaign
  name: string;
  lineItemType: number; // Beeswax type id: 0 banner | 1 video | 2 native
  isCtvLineItem: boolean; // true only when the user picks CTV (source must be CTV)
  copyTargeting: boolean;
  copyCreatives: boolean;
}

export interface DuplicateLineItemResponse {
  success: boolean;
  payload: { id: number; [key: string]: any };
}

export function duplicateLineItem(
  sourceLineItemId: number | string,
  payload: DuplicateLineItemPayload
) {
  return client.post<DuplicateLineItemResponse>(
    `/line-item/display/${sourceLineItemId}/duplicate`,
    payload
  );
}
